import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

// Generic horizontal stacked-bar list grouped by one dimension
// (CSE incharge / sales unit / region / sales incharge). Each row shows
// approved (emerald) + pending (amber) + rejected (red) segments and an
// overdue badge. Rows are clickable when onItemClick is provided (drill-down).
// columns=2 renders a two-column grid (keeps long lists compact).
const WaiverGroupBarCard = ({ title, Icon, iconColor, data, loading, error, labelKey, maxRows, subLabelKey, columns, onItemClick, bodyMaxHeight, fullHeight }) => {
  const rows = (data || []).slice(0, maxRows);
  const maxTotal = Math.max(...rows.map(r => r.total), 1);

  return (
    // padding="none" — this card builds its own flush header/body; Card's
    // default body padding would double up and inflate the layout.
    // tw-h-full only when paired in a grid (fullHeight) — a standalone
    // full-width card must hug its content, not stretch.
    <Card padding="none" className={`tw-border tw-border-gray-200 tw-shadow-sm ${fullHeight ? 'tw-h-full' : ''}`}>
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
        <Icon size={15} className={iconColor} />
        <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">{title}</h3>
        {rows.length > 0 && (
          <span className="tw-text-[10px] tw-text-gray-400">top {rows.length}</span>
        )}
        {onItemClick && rows.length > 0 && (
          <span className="tw-text-[10px] tw-text-gray-300 tw-ml-auto tw-italic tw-hidden sm:tw-inline">click a row for details</span>
        )}
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
      ) : error || rows.length === 0 ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10">No data</p>
      ) : (
        <div
          className={`tw-p-4 ${bodyMaxHeight ? 'tw-overflow-y-auto' : ''} ${columns === 2 ? 'tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-3 tw-content-start' : 'tw-space-y-2.5'}`}
          style={bodyMaxHeight ? { maxHeight: bodyMaxHeight } : undefined}>
          {rows.map((r, i) => {
            const widthPct = (r.total / maxTotal) * 100;
            const seg = (v) => (r.total > 0 ? (v / r.total) * widthPct : 0);
            return (
              <div
                key={`${r[labelKey]}-${i}`}
                onClick={onItemClick ? () => onItemClick(r) : undefined}
                className={onItemClick ? 'tw-cursor-pointer tw-rounded tw-px-1.5 tw-py-1 -tw-mx-1.5 hover:tw-bg-slate-50 tw-transition-colors' : ''}
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-0.5">
                  <span className="tw-text-xs tw-font-medium tw-text-gray-700 tw-truncate" title={r[labelKey]}>
                    {i + 1}. {r[labelKey]}
                    {subLabelKey && r[subLabelKey] && (
                      <span className="tw-text-[10px] tw-text-gray-400 tw-ml-1.5">{r[subLabelKey]}</span>
                    )}
                  </span>
                  <span className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
                    {r.overdue > 0 && (
                      <span className="tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-orange-100 tw-text-orange-600 tw-whitespace-nowrap">
                        {fmt(r.overdue)} overdue
                      </span>
                    )}
                    <span className="tw-text-xs tw-font-bold tw-text-gray-900 tw-tabular-nums">{fmt(r.total)}</span>
                  </span>
                </div>
                <div className="tw-flex tw-h-3 tw-rounded-xl tw-bg-gray-100 tw-overflow-hidden"
                  title={`Approved ${fmt(r.approved)} · Pending ${fmt(r.pending)} · Rejected ${fmt(r.rejected)}`}>
                  <div style={{ width: `${seg(r.approved)}%`, background: '#10B981' }} />
                  <div style={{ width: `${seg(r.pending)}%`,  background: '#F59E0B' }} />
                  <div style={{ width: `${seg(r.rejected)}%`, background: '#EF4444' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

WaiverGroupBarCard.propTypes = {
  title: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  iconColor: PropTypes.string,
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.any,
  labelKey: PropTypes.string.isRequired,
  subLabelKey: PropTypes.string,
  maxRows: PropTypes.number,
  columns: PropTypes.number,
  onItemClick: PropTypes.func,
  bodyMaxHeight: PropTypes.string,
  fullHeight: PropTypes.bool,
};
WaiverGroupBarCard.defaultProps = {
  iconColor: 'tw-text-primary-500', data: [], loading: false,
  error: null, subLabelKey: null, maxRows: 10, columns: 1, onItemClick: null,
  bodyMaxHeight: null, fullHeight: true,
};

export default WaiverGroupBarCard;
